import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Section, SectionHeading, Reveal } from "@/components/primitives";
import { trackEvent } from "@/lib/api";
import { ShieldCheck, ExternalLink, Search, Copy, Check, CircleCheck, Clock } from "lucide-react";

const EXPLORERS = {
  ethereum: { label: "Ethereum", base: "https://etherscan.io/tx/", tag: "ERC-20" },
  tron: { label: "Tron", base: "https://tronscan.org/#/transaction/", tag: "TRC-20" },
  bsc: { label: "BNB Chain", base: "https://bscscan.com/tx/", tag: "BEP-20" },
  arbitrum: { label: "Arbitrum", base: "https://arbiscan.io/tx/", tag: "L2" },
  base: { label: "Base", base: "https://basescan.org/tx/", tag: "L2" },
  polygon: { label: "Polygon", base: "https://polygonscan.com/tx/", tag: "L2" },
};

const DEMO_TXNS = [
  { chain: "ethereum", hash: "0x9f2c4e8a17b3d6f0c9e5a2b7d84f1c0369ae5b2d7c4f9018a63e2d5b7c1f0a41e", ts: "2026-06-14 09:42:11 UTC", amount: "1,250.00 USDT", status: "confirmed" },
  { chain: "tron", hash: "5d1a9c3f7e0b482d6a1f8c4e9b2d7a05c3f6e1b8d4a7c0f293e5b6d1a8c4f0e27", ts: "2026-06-14 08:15:47 UTC", amount: "480.00 USDT", status: "confirmed" },
  { chain: "arbitrum", hash: "0x3c7f1e9a4d8b2065c0f3e6a9b1d7c4082e5f9a3b6d0c1f78493a2e5d6b8c0f19a", ts: "2026-06-13 21:03:22 UTC", amount: "3,000.00 USDC", status: "confirmed" },
  { chain: "base", hash: "0x7a1d4f9c2e6b80513d9f2a6c4e8b1079f3a5d2c6e9b4017a83f6d2c5b9e0a4f13", ts: "2026-06-13 17:29:58 UTC", amount: "920.50 USDC", status: "confirmed" },
  { chain: "bsc", hash: "0x2e9b4c7a1f6d3085e0c9a4f2b7d6103c8f5e2a9d4b7c0163f9e5a2d6c8b1f0e34", ts: "2026-06-13 11:47:05 UTC", amount: "5,600.00 USDT", status: "confirmed" },
];

const short = (h) => (h.length > 18 ? `${h.slice(0, 10)}…${h.slice(-6)}` : h);

export default function Verify() {
  const [chain, setChain] = useState("ethereum");
  const [hash, setHash] = useState("");
  const [copied, setCopied] = useState(null);

  useEffect(() => { trackEvent("page_view", { page: "verify" }); }, []);

  const verify = () => {
    if (!hash.trim()) return;
    trackEvent("verify_tx", { chain });
    window.open(EXPLORERS[chain].base + hash.trim(), "_blank", "noopener,noreferrer");
  };
  const copy = async (h) => {
    try { await navigator.clipboard?.writeText(h); } catch { /* ignore */ }
    setCopied(h); setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div>
      <PageHeader
        eyebrow="On-Chain Transparency"
        title="Don't trust. Verify."
        sub="Where activity is recorded on public blockchains, transactions can be independently verified on the relevant explorer. Confidence through transparency — not claims."
      />

      {/* Verify-your-own tool */}
      <Section grid>
        <Reveal>
          <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-[#12131A] to-[#0A0B10] p-6 md:p-8 qs-aurora" data-testid="verify-tool">
            <div className="relative">
              <div className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#2DD4BF] mb-5">
                <Search size={15} strokeWidth={1.5} /> Verify a Transaction
              </div>
              <div className="grid md:grid-cols-[180px_1fr_auto] gap-3">
                <select
                  value={chain} onChange={(e) => setChain(e.target.value)} data-testid="verify-chain"
                  className="rounded-xl border border-white/10 bg-[#08090F] px-4 py-3 text-sm text-white focus:border-[#2DD4BF]/50 focus:outline-none"
                >
                  {Object.entries(EXPLORERS).map(([k, v]) => (
                    <option key={k} value={k} className="bg-[#08090F]">{v.label} · {v.tag}</option>
                  ))}
                </select>
                <input
                  value={hash} onChange={(e) => setHash(e.target.value)} data-testid="verify-hash-input"
                  onKeyDown={(e) => e.key === "Enter" && verify()}
                  placeholder="Paste a transaction hash (0x… or Tron ID)"
                  className="rounded-xl border border-white/10 bg-[#08090F] px-4 py-3 text-sm text-white font-mono focus:border-[#2DD4BF]/50 focus:outline-none"
                />
                <button
                  onClick={verify} disabled={!hash.trim()} data-testid="verify-submit"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0E9E90] text-white font-medium px-6 py-3 shadow-[0_0_20px_rgba(14,158,144,0.3)] hover:bg-[#1E4BCC] disabled:opacity-40 transition-colors"
                >
                  Verify <ExternalLink size={15} strokeWidth={2} />
                </button>
              </div>
              <p className="mt-3 text-xs text-slate-500">Opens the official block explorer for the selected network in a new tab. We never take custody of your keys.</p>
            </div>
          </div>
        </Reveal>
      </Section>

      {/* Recent on-chain activity (demo) */}
      <Section grid>
        <SectionHeading
          eyebrow="Recent Activity"
          title="Recent on-chain settlements."
          sub="Representative records for demonstration. Connect Q-Core's live settlement feed to display verifiable production transactions."
          className="mb-8"
        />
        <div className="rounded-2xl border border-white/5 bg-[#0A0B10] overflow-hidden" data-testid="verify-table">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/5">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-white">Settlement Ledger</span>
            <span className="font-mono text-[10px] uppercase tracking-widest rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-1 text-amber-300">Demo / Sample</span>
          </div>
          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              <div className="grid grid-cols-12 gap-2 px-5 py-2.5 border-b border-white/5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                <span className="col-span-2">Network</span>
                <span className="col-span-4">Transaction Hash</span>
                <span className="col-span-3">Timestamp</span>
                <span className="col-span-2 text-right">Amount</span>
                <span className="col-span-1 text-right">Verify</span>
              </div>
              <div className="divide-y divide-white/5">
                {DEMO_TXNS.map((t) => {
                  const ex = EXPLORERS[t.chain];
                  return (
                    <div key={t.hash} className="grid grid-cols-12 gap-2 px-5 py-3.5 items-center text-xs" data-testid={`verify-row-${t.chain}`}>
                      <span className="col-span-2">
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-slate-200">
                          <span className="h-1.5 w-1.5 rounded-full bg-[#2DD4BF]" /> {ex.label}
                        </span>
                      </span>
                      <span className="col-span-4 flex items-center gap-2 font-mono text-[#2DD4BF]">
                        {short(t.hash)}
                        <button onClick={() => copy(t.hash)} className="text-slate-500 hover:text-white transition-colors" aria-label="Copy hash">
                          {copied === t.hash ? <Check size={13} className="text-[#00E676]" /> : <Copy size={13} />}
                        </button>
                      </span>
                      <span className="col-span-3 font-mono text-slate-400 flex items-center gap-1.5"><Clock size={12} /> {t.ts}</span>
                      <span className="col-span-2 text-right font-mono text-white">{t.amount}</span>
                      <span className="col-span-1 text-right">
                        <a
                          href={ex.base + t.hash} target="_blank" rel="noopener noreferrer"
                          data-testid={`verify-explorer-${t.chain}`}
                          className="inline-flex items-center gap-1 text-[#2DD4BF] hover:text-white transition-colors"
                        >
                          <ExternalLink size={14} />
                        </a>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section grid>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: ShieldCheck, t: "Non-custodial", d: "Verification never requires your private keys. You confirm records independently on public explorers." },
            { icon: CircleCheck, t: "Publicly auditable", d: "On-chain settlements carry an immutable hash anyone can inspect on the relevant network." },
            { icon: ExternalLink, t: "Official explorers", d: "Links route to the canonical explorer for each network — Etherscan, Tronscan, Arbiscan and more." },
          ].map((c) => (
            <div key={c.t} className="rounded-xl border border-white/5 bg-[#0A0B10] p-6">
              <span className="grid place-items-center h-11 w-11 rounded-lg border border-[#0E9E90]/30 bg-[#0E9E90]/10 text-[#2DD4BF] mb-4"><c.icon size={20} strokeWidth={1.5} /></span>
              <h3 className="text-white font-bold">{c.t}</h3>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
